/**
 * UK postcode → lat/lng + admin metadata via postcodes.io.
 *
 * Free, no API key required, generous fair-use limits. Enough for site-setup
 * geofencing — we only call it once per site at create/edit time.
 *
 *   const r = await lookupPostcode('HG2 7XS');
 *   r?.latitude, r?.longitude, r?.admin_district
 */

export interface PostcodeResult {
  postcode: string;
  latitude: number;
  longitude: number;
  admin_district: string | null;
  admin_ward: string | null;
  parish: string | null;
  region: string | null;
  country: string | null;
}

const POSTCODE_RE = /^[A-Z]{1,2}[0-9][A-Z0-9]?\s*[0-9][A-Z]{2}$/i;

export function isValidPostcodeFormat(pc: string): boolean {
  return POSTCODE_RE.test(pc.trim());
}

export async function lookupPostcode(postcode: string): Promise<PostcodeResult | null> {
  const trimmed = postcode.trim().toUpperCase();
  if (!trimmed) return null;
  if (!POSTCODE_RE.test(trimmed)) return null;
  const res = await fetch(
    `https://api.postcodes.io/postcodes/${encodeURIComponent(trimmed)}`
  );
  if (res.status === 404) return null;
  if (!res.ok) throw new Error(`Postcode lookup failed (${res.status}).`);
  const data = await res.json();
  if (data.status !== 200 || !data.result) return null;
  const r = data.result;
  return {
    postcode: r.postcode as string,
    latitude: r.latitude as number,
    longitude: r.longitude as number,
    admin_district: (r.admin_district as string | null) ?? null,
    admin_ward: (r.admin_ward as string | null) ?? null,
    parish: (r.parish as string | null) ?? null,
    region: (r.region as string | null) ?? null,
    country: (r.country as string | null) ?? null,
  };
}
